import { Col, Row } from 'antd';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import { Customer } from 'store/customers/customer';
import { extractPhoneNumber } from 'utils/phoneNumber';
import { FormValues } from './types';

export const FormRow = ({ children }: { children: React.ReactNode }) => (
    <Row style={{ width: '100%' }} justify="space-between" gutter={[40, 16]}>
        {children}
    </Row>
);

export const FormCol = ({ children }: { children: React.ReactNode }) => (
    <Col sm={24} md={12}>
        {children}
    </Col>
);

export const FieldLabel = ({
    htmlFor,
    required,
    children,
}: {
    htmlFor?: string;
    required?: boolean;
    children: React.ReactNode;
}) => (
    <label htmlFor={htmlFor} style={{ marginBottom: 6, display: 'block' }}>
        {required && (
            <>
                <LabelRequiredSymbol />{' '}
            </>
        )}
        {children}
    </label>
);

export const mapCustomerToFormValues = (customer: Customer): FormValues => {
    const { countryCode, phoneNumber } = extractPhoneNumber(customer.contact?.phoneNumber);
    const { customerDetails } = customer;

    return {
        customerName: customer.name ?? null,
        referenceCode: customer.referenceCode ?? null,
        eori: customer.eori ?? null,
        tan: customer.traderAccountNumber ?? null,
        deferredAccountNumber: customer.deferredAccountNumber ?? null,
        vatNumber: customer.vatNumber ?? null,
        addressLine1: customer.address?.addressLine1 ?? null,
        addressLine2: customer.address?.addressLine2 ?? null,
        country: customer.address?.country ?? null,
        postCode: customer.address?.postCode ?? null,
        city: customer.address?.city ?? null,
        contactName: customer.contact?.name ?? null,
        email: customer.contact?.email ?? null,
        phoneNumber: phoneNumber || null,
        phoneCountryCode: countryCode || null,
        dutyPayer: customerDetails?.dutyPayer ?? null,
        vatTreatment: customerDetails?.vatTreatment ?? null,
        vatExemption: customerDetails?.vatExemption ?? null,
        recipientName: customerDetails?.billingName ?? null,
        billingEmail: customerDetails?.billingEmail ?? null,
        thirdPartyBilling: customerDetails?.thirdParty ?? null,
        billingCustomerCode: customerDetails?.billingCustomerCode ?? null,
        specialInstructions: customer.specialInstructions ?? null,
        hasAEP5Authorization: customerDetails?.confirmedTanUsage ?? null,
    };
};

export const mapFormValuesToParty = (values: FormValues, customer: Customer): Customer => ({
    ...customer,
    name: values.customerName ?? customer.name,
    referenceCode: values.referenceCode ?? undefined,
    eori: values.eori ?? customer.eori,
    traderAccountNumber: values.tan ?? undefined,
    deferredAccountNumber: values.deferredAccountNumber ?? undefined,
    vatNumber: values.vatNumber ?? undefined,
    address: {
        addressLine1: values.addressLine1 ?? customer.address.addressLine1,
        addressLine2: values.addressLine2 ?? null,
        country: values.country ?? customer.address.country,
        postCode: values.postCode ?? customer.address.postCode,
        city: values.city ?? customer.address.city,
    },
    contact: {
        ...customer.contact,
        name: values.contactName ?? undefined,
        email: values.email ?? undefined,
        phoneNumber:
            values.phoneCountryCode && values.phoneNumber
                ? `${values.phoneCountryCode}${values.phoneNumber}`
                : values.phoneNumber ?? undefined,
    },
    customerDetails: {
        // Keep the id (and any field the overview form doesn't expose) so an update doesn't drop it
        ...customer.customerDetails,
        dutyPayer: values.dutyPayer ?? undefined,
        confirmedTanUsage: values.hasAEP5Authorization ?? undefined,
        vatTreatment: values.vatTreatment ?? undefined,
        vatExemption: values.vatExemption ?? undefined,
        billingName: values.recipientName ?? undefined,
        billingEmail: values.billingEmail ?? undefined,
        thirdParty: values.thirdPartyBilling ?? undefined,
        billingCustomerCode: values.billingCustomerCode ?? undefined,
    },
    specialInstructions: values.specialInstructions ?? undefined,
});
